import { Box } from "@mui/material";
import React, { useContext, useMemo } from "react";
import {
  REFERENCE_LABEL_BG,
  REFERENCE_LABEL_FONT_SIZE,
  REFERENCE_LABEL_Z_INDEX,
  REFERENCE_SELECTION_COLOR,
  SELECTION_BORDER_RADIUS,
} from "../../Constants/DesignerModeStyles";
import { VariableNames } from "../../Constants/VariableNames";
import BundleContext from "../../Contexts/BundleContext";
import { useChildContext } from "../../Hooks/useChildContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { ReferenceComponentModel } from "../../Models/ReferenceComponentModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const ReferenceComponent: React.FC<IComponentProps<ReferenceComponentModel>> = ({ parent, component, context, componentPath = [] }) => {
  const bundle = useContext(BundleContext);
  const sharedComponent = bundle.SharedComponentById[component.SharedComponent?.Id];

  // Evaluate the Input expression using the outer context
  const [inputValue] = useObservedExpression(component.Input, context, "ReferenceComponent");

  const sharedContextVariables = useMemo(() => ({ [VariableNames.Input]: inputValue }), [inputValue]);

  const sharedContext = useChildContext(context, sharedContextVariables);

  // Use enhanced selection hook with reference variant and additional data attributes
  const selectionProps = useComponentSelection({
    component,
    componentPath,
    variant: "reference",
    additionalDataAttributes: component.SharedComponent?.Id ? { "data-shared-component-id": component.SharedComponent.Id } : {},
  });

  const layoutSx = getComponentLayout(component);

  // Combine layout styles with selection styles from hook
  const combinedSx = useMemo(() => {
    if (!selectionProps.sx) {
      return layoutSx || {};
    }
    // Combine both - selectionProps.sx is already an array from the hook
    const layoutArray = layoutSx ? [layoutSx] : [];
    return [...layoutArray, ...(Array.isArray(selectionProps.sx) ? selectionProps.sx : [selectionProps.sx])];
  }, [layoutSx, selectionProps.sx]);

  return (
    // Do not render anything if there's an input expression but it hasn't produced a value yet
    (!component.Input || inputValue) && (
      <Box {...selectionProps} sx={combinedSx}>
        {IsDesignMode && sharedComponent && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              fontSize: REFERENCE_LABEL_FONT_SIZE,
              fontWeight: "bold",
              color: REFERENCE_SELECTION_COLOR,
              backgroundColor: REFERENCE_LABEL_BG,
              padding: "1px 4px",
              borderRadius: `0 0 ${SELECTION_BORDER_RADIUS} 0`,
              zIndex: REFERENCE_LABEL_Z_INDEX,
              pointerEvents: "none",
              lineHeight: 1,
            }}
          >
            REF: {sharedComponent.Name}
          </Box>
        )}
        {sharedComponent ? (
          <ComponentFactory
            key={`${sharedContext.StableId}-${sharedComponent.Component.Id}`}
            parent={component}
            component={sharedComponent.Component}
            context={sharedContext}
            componentPath={[...componentPath, sharedComponent.Component.Id]}
          />
        ) : !component.SharedComponent?.Id && IsDesignMode ? (
          <div>Select a SharedComponent</div>
        ) : component.SharedComponent?.Id ? (
          <div>Shared component not found: {component.SharedComponent.Id}</div>
        ) : null}
      </Box>
    )
  );
};

export default ReferenceComponent;
